import "p2"
import "phaser"
import * as Phaser from "phaser-ce"
import "pixi"
import { AppType } from "../common/AppType"
import { CCUtils } from "../common/CCUtils"
import { DEV_STATE } from "../common/LocalConfig"
import { log } from "../common/Logging"
import { ActivityKey } from "../shared/models/ActivityKey"
import { AddictionRoadState } from "./activities/addiction-road/AddictionRoadState"
import { CravingsState } from "./activities/addiction/cravings-and-withdrawal/CravingsState"
import { AddictionMainState } from "./activities/addiction/main/AddictionMainState"
import { MentalState } from "./activities/addiction/mental-addiction/MentalState"
import { NicotineState } from "./activities/addiction/nicotine-and-addiction/NicotineState"
import { PhysicalState } from "./activities/addiction/physical-addiction/PhysicalState"
import { CampCravingsExit } from "./activities/camp-cravings/CampCravingsExit"
import { CampCravingsState } from "./activities/camp-cravings/CampCravingsState"
import { DefinitionState } from "./activities/definition/DefinitionState"
import { DrTimeState } from "./activities/dr-time/DrTimeState"
import { Ecig101State } from "./activities/ecig-101/Ecig101State"
import { EveryCig2State } from "./activities/every-cig/EveryCig2State"
import { EveryCigState } from "./activities/every-cig/EveryCigState"
import { KidsChoiceExit } from "./activities/kids-choice/KidsChoiceExit"
import { KidsChoicePartOneState } from "./activities/kids-choice/KidsChoicePartOneState"
import { KidsChoicePartTwoState } from "./activities/kids-choice/KidsChoicePartTwoState"
import { MakeFriendState } from "./activities/make-friend/MakeFriendState"
import { MakeYourOwnVaperPartOneState } from "./activities/make-vaper/part-1/MakeYourOwnVaperPartOneState"
import { MakeYourOwnVaperPartTwoState } from "./activities/make-vaper/part-2/MakeYourOwnVaperPartTwoState"
import { MakeAVideoState } from "./activities/make-video/MakeAVideoState"
import { MysteryState } from "./activities/mystery/MysteryState"
import { PersonalityState } from "./activities/personality/PersonalityState"
import { PlaygroundState } from "./activities/playground/PlaygroundState"
import { PongOrAddictionRoadExit } from "./activities/pong/PongOrAddictionRoadExit"
import { PongState } from "./activities/pong/PongState"
import { RealityCheckState } from "./activities/reality-check/RealityCheckState"
import { VaporState } from "./activities/second-hand/effects-of-vapor/VaporState"
import { LongTermState } from "./activities/second-hand/long-term/LongTermState"
import { SecondHandState } from "./activities/second-hand/main/SecondHandState"
import { SecondVsDirectState } from "./activities/second-hand/second-vs-direct/SecondVsDirectState"
import { ShortTermState } from "./activities/second-hand/short-term/ShortTermState"
import { SummaryGrade6State } from "./activities/summary-6/SummaryGrade6State"
import { SummaryState } from "./activities/summary/SummaryState"
import { HeroStartState } from "./activities/super-hero/HeroStartState"
import { HeroState } from "./activities/super-hero/HeroState"
import { TimeMachineState } from "./activities/time-machine/TimeMachineState"
import { ChewTourState } from "./activities/tobacco-tour/chew-tour/ChewTourState"
import { CigTourState } from "./activities/tobacco-tour/cig-tour/CigTourState"
import { EcigTourState } from "./activities/tobacco-tour/ecig-tour/EcigTourState"
import { TobaccoTourReviewState } from "./activities/tobacco-tour/TobaccoTourReviewState"
import { TobaccoTourState } from "./activities/tobacco-tour/TobaccoTourState"
import { Transitions } from "./activities/transitions/Transitions"
import { TruthOrDareState } from "./activities/truth-or-dare/TruthOrDareState"
import { WheelOfMisfortuneExit } from "./activities/wheel-of-misfortune/WheelOfMisfortuneExit"
import { WheelOfMisfortuneState } from "./activities/wheel-of-misfortune/WheelOfMisfortuneState"
import { TransitionState } from "./GameState"
import { MapState } from "./map/MapState"
import { Scale } from "./utils/Scale"
import { WorldRepo } from "./world/WorldRepo"
import { WorldState } from "./world/WorldState"

const startState: Phaser.State = CCUtils.envMode && DEV_STATE ? new DEV_STATE() : new MapState()

export class MainGame extends Phaser.Game {

    constructor() {
        super(document.documentElement.clientWidth, document.documentElement.clientHeight,
            Phaser.CANVAS, AppType.PHASER, startState)

        Scale.resizeScreen(this)
        log.debug(`Dev is ${CCUtils.envMode}`)
        log.debug(
            "Screen width: " + Scale.width() +
            "\nScreen height: " + Scale.height() +
            "\nScale amount: " + Scale.scaleAmount())

        if (CCUtils.envMode && !WorldRepo.isDebugModeSaved()) {
            WorldRepo.setDebugMode(true)
        }

        // Grade 5
        this.state.add(ActivityKey.SECOND_HAND, new SecondHandState())
        this.state.add(TransitionState.LONG_TERM, new LongTermState())
        this.state.add(TransitionState.SHORT_TERM, new ShortTermState())
        this.state.add(TransitionState.EFFECTS_OF_VAPOR, new VaporState())
        this.state.add(TransitionState.SECOND_VS_DIRECT, new SecondVsDirectState())
        this.state.add(ActivityKey.ADDICTION_PONG, new PongState())
        this.state.add(TransitionState.SUPER_HERO_CORE, new HeroState())
        this.state.add(ActivityKey.SUPER_HERO, new HeroStartState())
        this.state.add(TransitionState.WORLD, new WorldState())
        this.state.add(TransitionState.MAP, new MapState())
        this.state.add(ActivityKey.ADDICTION_MAIN, new AddictionMainState())
        this.state.add(TransitionState.ADDICTION_MENTAL, new MentalState())
        this.state.add(TransitionState.ADDICTION_PHYSICAL, new PhysicalState())
        this.state.add(TransitionState.ADDICTION_CRAVINGS_AND_WITHDRAWAL, new CravingsState())
        this.state.add(TransitionState.ADDICTION_NICOTINE, new NicotineState())
        this.state.add(ActivityKey.DEFINITION, new DefinitionState())
        this.state.add(TransitionState.ECIG_TOUR, new EcigTourState())
        this.state.add(ActivityKey.ECIG_101, new Ecig101State())
        this.state.add(ActivityKey.EVERY_CIG, new EveryCigState())
        this.state.add(ActivityKey.EVERY_CIG_2, new EveryCig2State())
        this.state.add(ActivityKey.REALITY_CHECK, new RealityCheckState())
        this.state.add(ActivityKey.CAMP_CRAVINGS, new CampCravingsState())
        this.state.add(ActivityKey.TOBACCO_TOUR, new TobaccoTourState())
        this.state.add(TransitionState.TOBACCO_TOUR_CHEW, new ChewTourState())
        this.state.add(TransitionState.TOBACCO_TOUR_CIG, new CigTourState())
        this.state.add(ActivityKey.TRUTH_OR_DARE, new TruthOrDareState())
        this.state.add(ActivityKey.TIME_MACHINE, new TimeMachineState())
        this.state.add(ActivityKey.WHEEL_OF_MISFORTUNE, new WheelOfMisfortuneState())
        this.state.add(ActivityKey.PLAYGROUND, new PlaygroundState())
        this.state.add(TransitionState.TOBACCO_TOUR_REVIEW, new TobaccoTourReviewState())
        this.state.add(ActivityKey.MAKE_YOUR_OWN_VAPER_PT_1, new MakeYourOwnVaperPartOneState())
        this.state.add(ActivityKey.MAKE_YOUR_OWN_VAPER_PT_2, new MakeYourOwnVaperPartTwoState())
        this.state.add(ActivityKey.MAKE_A_VIDEO, new MakeAVideoState())
        this.state.add(ActivityKey.PERSONALITY, new PersonalityState())
        this.state.add(ActivityKey.KIDS_CHOICE, new KidsChoicePartOneState())
        this.state.add(ActivityKey.KIDS_CHOICE_2, new KidsChoicePartTwoState())
        this.state.add(ActivityKey.SUMMARY, new SummaryState())

        // Grade 6
        this.state.add(ActivityKey.ECIG_201, new Ecig101State())
        this.state.add(ActivityKey.MYSTERY, new MysteryState())
        this.state.add(ActivityKey.DR_TIME, new DrTimeState())
        this.state.add(ActivityKey.ADDICTION_201, new AddictionMainState())
        this.state.add(ActivityKey.ADDICTION_ROAD, new AddictionRoadState())
        this.state.add(ActivityKey.MAKE_A_FRIEND, new MakeFriendState())
        this.state.add(ActivityKey.SUMMARY_6, new SummaryGrade6State())

        // Testimonial Outros
        this.state.add(TransitionState.KIDS_CHOICE_EXIT, new KidsChoiceExit())
        this.state.add(TransitionState.ADDICTION_PONG_EXIT, new PongOrAddictionRoadExit(true))
        this.state.add(TransitionState.CAMP_CRAVINGS_EXIT, new CampCravingsExit())
        this.state.add(TransitionState.WHEEL_EXIT, new WheelOfMisfortuneExit())
        this.state.add(TransitionState.ADDICTION_ROAD_EXIT, new PongOrAddictionRoadExit(false))

        Transitions.registerTransitions(this, [
            // Grade 5, Lesson 1
            {
                activity: ActivityKey.ECIG_101,
                activityLocation: "ecig-101",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.TOBACCO_TOUR,
                activityLocation: "tobacco-tour",
                outroVideoButtonExit: true,
                hasTestimonials: false
            },

            // Grade 5, Lesson 2
            {
                activity: ActivityKey.KIDS_CHOICE,
                activityLocation: "kids-choice",
                outroVideoButtonExit: false,
                hasTestimonials: true
            },
            {
                activity: ActivityKey.MAKE_YOUR_OWN_VAPER_PT_1,
                activityLocation: "make-vaper",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.KIDS_CHOICE_2,
                activityLocation: "kids-choice-2",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.REALITY_CHECK,
                activityLocation: "reality-check",
                outroVideoButtonExit: true,
                hasTestimonials: false
            },

            // Grade 5, Lesson 3
            {
                activity: ActivityKey.MAKE_YOUR_OWN_VAPER_PT_2,
                activityLocation: "make-vaper-2",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.ADDICTION_MAIN,
                activityLocation: "addiction",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.ADDICTION_PONG,
                activityLocation: "pong",
                outroVideoButtonExit: true,
                hasTestimonials: true
            },

            // Grade 5, Lesson 4
            {
                activity: ActivityKey.CAMP_CRAVINGS,
                activityLocation: "camp-cravings",
                outroVideoButtonExit: false,
                hasTestimonials: true
            },
            {
                activity: ActivityKey.PERSONALITY,
                activityLocation: "personality",
                outroVideoButtonExit: true,
                hasTestimonials: false
            },

            // Grade 5, Lesson 5
            {
                activity: ActivityKey.SECOND_HAND,
                activityLocation: "second-hand",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.SUPER_HERO,
                activityLocation: "super-hero",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.TRUTH_OR_DARE,
                activityLocation: "truth-or-dare",
                outroVideoButtonExit: true,
                hasTestimonials: false
            },

            // Grade 5, Lesson 6
            {
                activity: ActivityKey.MAKE_A_VIDEO,
                activityLocation: "make-video",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.WHEEL_OF_MISFORTUNE,
                activityLocation: "wheel-of-misfortune",
                outroVideoButtonExit: false,
                hasTestimonials: true
            },
            {
                activity: ActivityKey.EVERY_CIG,
                activityLocation: "every-cig",
                outroVideoButtonExit: true,
                hasTestimonials: false
            },

            // Grade 5, Lesson 7
            {
                activity: ActivityKey.DEFINITION,
                activityLocation: "definition",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.MAZE,
                activityLocation: "maze",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.EVERY_CIG_2,
                activityLocation: "every-cig-2",
                outroVideoButtonExit: true,
                hasTestimonials: false
            },

            // Grade 5, Lesson 8
            {
                activity: ActivityKey.TIME_MACHINE,
                activityLocation: "time-machine",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.SUMMARY,
                activityLocation: "summary",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.PLAYGROUND,
                activityLocation: "playground",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },

            // Grade 6, Lesson 1
            {
                activity: ActivityKey.ECIG_201,
                activityLocation: "ecig-201",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.MYSTERY,
                activityLocation: "mystery",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.DR_TIME,
                activityLocation: "dr-time",
                outroVideoButtonExit: true,
                hasTestimonials: false
            },

            // Grade 6, Lesson 2
            {
                activity: ActivityKey.ADDICTION_201,
                activityLocation: "addiction-201",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.ADDICTION_ROAD,
                activityLocation: "addiction-road",
                outroVideoButtonExit: false,
                hasTestimonials: true
            },
            {
                activity: ActivityKey.MAKE_A_FRIEND,
                activityLocation: "make-friend",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
            {
                activity: ActivityKey.SUMMARY_6,
                activityLocation: "summary-6",
                outroVideoButtonExit: false,
                hasTestimonials: false
            },
        ])

    }

}
